import { useState } from "react";
import { Link, useLocation, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { BrandMark } from "@/components/BrandMark";

const HERO = "https://images.unsplash.com/photo-1566231270035-0aaca1fd2bbf?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function AuthPage({ mode }) {
  const { user, login, register } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "", account_type: "member", business_name: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();
  const isLogin = mode === "login";
  const home = (u) => (u.role === "business" ? "/business" : u.onboarded ? null : "/onboarding");
  if (user) return <Navigate to={home(user) || "/"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const u = isLogin ? await login({ email: form.email, password: form.password }) : await register(form);
      nav(home(u) || loc.state?.from || "/");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const field = (k, label, type = "text") => (
    <label className="block">
      <span className="text-xs uppercase tracking-wider font-semibold text-stone-500">{label}</span>
      <input data-testid={`auth-${k}-input`} type={type} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} required
        className="mt-1.5 w-full h-11 rounded-xl border border-stone-300 bg-white px-3.5 focus:outline-none focus:ring-2 focus:ring-[#2F7D55]/40" />
    </label>
  );

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block grain">
        <img src={HERO} alt="Sunlight through a green forest path" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F2A1F]/90 via-[#0F2A1F]/30 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-amber-200">Free time, well spent</p>
          <h2 className="mt-3 text-5xl font-extrabold leading-tight">What could you do<br />with your free time?</h2>
          <p className="mt-4 max-w-md text-stone-200">Pick a window or one thing you already want to do. Outbound builds a flexible outing around it, so you can get out and enjoy it.</p>
        </div>
      </div>
      <div className="relative flex items-center justify-center p-6 sm:p-12 bg-[#0F2A1F] grain overflow-hidden" data-testid="auth-forest-panel">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#2F7D55]/40 blur-3xl" />
        <div className="absolute -bottom-32 -left-16 w-[28rem] h-[28rem] rounded-full bg-[#B8892B]/15 blur-3xl" />
        <form onSubmit={submit} className="relative w-full max-w-sm space-y-5 rise bg-[#F3F6EF]/95 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-[#B8892B]/30" data-testid="auth-form">
          <BrandMark size={52} />
          <div>
            <h1 className="text-4xl font-extrabold tracking-tight">{isLogin ? "Welcome back" : "Create your account"}</h1>
            <p className="mt-2 text-stone-600 text-sm">{isLogin ? "Log in to plan your next outing." : "Two minutes to set up, then go live a little."}</p>
          </div>
          {!isLogin && (
            <div className="grid grid-cols-2 gap-1 p-1 bg-stone-100 rounded-xl" data-testid="auth-account-type">
              {[["member", "I'm exploring"], ["business", "I'm a local business"]].map(([v, l]) => (
                <button type="button" key={v} data-testid={`auth-type-${v}`} onClick={() => setForm({ ...form, account_type: v })}
                  className={`text-sm font-semibold rounded-lg py-2 transition-colors ${form.account_type === v ? "bg-white shadow-sm text-stone-900" : "text-stone-500"}`}>{l}</button>
              ))}
            </div>
          )}
          {!isLogin && form.account_type === "business" && field("business_name", "Business name")}
          {!isLogin && field("name", form.account_type === "business" ? "Your name (owner or manager)" : "Your name")}
          {field("email", "Email", "email")}
          {field("password", "Password", "password")}
          {error && <p data-testid="auth-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}
          <button data-testid="auth-submit-btn" disabled={busy} className="w-full h-11 bg-[#2F7D55] hover:bg-[#23603F] text-white font-bold rounded-xl transition-colors active:scale-[0.98] disabled:opacity-50">
            {busy ? "One moment…" : isLogin ? "Log in" : "Create account"}
          </button>
          <p className="text-sm text-stone-600">
            {isLogin ? "New here? " : "Already have an account? "}
            <Link data-testid="auth-switch-link" to={isLogin ? "/register" : "/login"} className="font-semibold text-[#23603F] hover:underline">{isLogin ? "Create an account" : "Log in"}</Link>
          </p>
          <Link to="/vision" data-testid="auth-vision-link" className="block text-xs text-stone-500 hover:text-stone-800">Read the product vision →</Link>
        </form>
      </div>
    </div>
  );
}
